// Issues filter bar — dropdowns for status, customer, industry, version
import { Button } from '@/components/ui/button'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Filter, X } from 'lucide-react'
import type { IssueStatus } from '@/types/database'

const STATUS_OPTIONS: { value: IssueStatus | '_all'; label: string }[] = [
  { value: '_all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_info', label: 'Waiting Info' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
]

interface IssuesFilterBarProps {
  statusFilter: IssueStatus | '_all'
  customerFilter: string
  industryFilter: string
  versionFilter: string
  onStatusChange: (v: IssueStatus | '_all') => void
  onCustomerChange: (v: string) => void
  onIndustryChange: (v: string) => void
  onVersionChange: (v: string) => void
  onClear: () => void
  filterOptions: { customers: string[]; industries: string[]; versions: string[] }
}

export function IssuesFilterBar(props: IssuesFilterBarProps): JSX.Element {
  const hasFilters = !!(
    props.statusFilter !== '_all' || props.customerFilter || props.industryFilter || props.versionFilter
  )

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-kaycoo-charcoal-2" />
        <span className="text-sm font-medium text-kaycoo-charcoal-2">Filters</span>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={props.onClear} className="text-xs text-kaycoo-burgundy hover:text-kaycoo-burgundy-dark">
            <X className="mr-1 h-3 w-3" /> Clear all
          </Button>
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <Select value={props.statusFilter} onValueChange={(v) => props.onStatusChange(v as IssueStatus | '_all')}>
          <SelectTrigger><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={props.customerFilter || '_all'} onValueChange={(v) => props.onCustomerChange(v === '_all' ? '' : v)}>
          <SelectTrigger><SelectValue placeholder="All Customers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Customers</SelectItem>
            {props.filterOptions.customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={props.industryFilter || '_all'} onValueChange={(v) => props.onIndustryChange(v === '_all' ? '' : v)}>
          <SelectTrigger><SelectValue placeholder="All Industries" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Industries</SelectItem>
            {props.filterOptions.industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={props.versionFilter || '_all'} onValueChange={(v) => props.onVersionChange(v === '_all' ? '' : v)}>
          <SelectTrigger><SelectValue placeholder="All Versions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Versions</SelectItem>
            {props.filterOptions.versions.map((v) => <SelectItem key={v} value={v}>D365 {v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
